import { Box, Button, CircularProgress, SxProps, Theme } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useExecuteModel } from 'shared/mutations';
import { Brands, MarginParamsChild, ResponseResult, Product } from 'shared/types';
import { buildChildModelPayload, buildFieldNameWithIndex, buildQuoteProduct } from 'shared/utility';
import { discountRecommendationBrands } from '../shared/constants';

const rootStyles: SxProps<Theme> = theme => ({
  position: 'absolute',
  height: '100%',
  width: '100%',
  zIndex: 1,
  backgroundColor: theme.palette.background.paper,
  opacity: 0.8,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

interface RecalculateProductProps {
  index: number;
  product: Product;
}
export function RecalculateProduct({ index, product }: RecalculateProductProps) {
  const { brand } = useParams<{ brand: Brands }>();
  const isDiscountBasedBrand: boolean = discountRecommendationBrands.includes(brand);
  const { setValue, getValues, clearErrors } = useFormContext();
  const executeMutation = useExecuteModel<MarginParamsChild, ResponseResult>('marginTarget');
  const buildFieldName = buildFieldNameWithIndex<Product>(index);

  const recalculateProduct = async () => {
    try {
      const {
        customer: {
          customerNumber,
          customerTier,
          customerType,
          competitionAdjustment,
          competitiveAdjustment,
          customerNumberAdjusted,
        },
        marginFloor,
      } = getValues();

      const modelPayload = buildChildModelPayload(
        {
          customerNumber,
          customerTier,
          customerType,
          competitionAdjustment,
          competitiveAdjustment,
          customerNumberAdjusted,
        },
        product,
        brand,
        marginFloor
      );
      const calcResponse = await executeMutation.mutateAsync(modelPayload);

      const newProduct = buildQuoteProduct(product, calcResponse.data, isDiscountBasedBrand, brand);
      setValue(buildFieldName('lastDiscountPaid'), newProduct.lastDiscountPaid);
      setValue(buildFieldName('competitiveDiscount'), newProduct.competitiveDiscount);
      setValue(buildFieldName('stretchDiscount'), newProduct.stretchDiscount);
      setValue(buildFieldName('targetDiscount'), newProduct.targetDiscount);
      setValue(buildFieldName('isCustomerVendorContracted'), newProduct.isCustomerVendorContracted);
      setValue(buildFieldName('lastMarginPaid'), newProduct.lastMarginPaid);
      setValue(buildFieldName('maxMargin'), newProduct.maxMargin);
      setValue(buildFieldName('competitiveMargin'), newProduct.competitiveMargin);
      setValue(buildFieldName('targetMargin'), newProduct.targetMargin);
      setValue(buildFieldName('marginAtList'), newProduct.marginAtList);
      setValue(buildFieldName('salesRepAdjustedMargin'), newProduct.salesRepAdjustedMargin);
      setValue(buildFieldName('revisedMargin'), newProduct.revisedMargin);
      setValue(buildFieldName('revisedPrice'), newProduct.revisedPrice);
      setValue(buildFieldName('displayPremiumHistorical'), newProduct.displayPremiumHistorical);
      setValue(buildFieldName('lastMarginPaidDate'), newProduct.lastMarginPaidDate);
      setValue(buildFieldName('recalculate'), false);
      setValue(buildFieldName('revisedDiscount'), newProduct.revisedDiscount);
      setValue(buildFieldName('revisedPrice'), newProduct.revisedPrice);
      setValue(buildFieldName('recommendedPrice'), newProduct.recommendedPrice);
      setValue(buildFieldName('extendedPrice'), newProduct.extendedPrice);
      setValue(buildFieldName('calculationId'), calcResponse.metadata.calculationId);
      setValue(buildFieldName('premiumHistoricalDiscount'), newProduct.premiumHistoricalDiscount);
      setValue(buildFieldName('lastDiscountPaidDate'), newProduct.lastDiscountPaidDate);
      setValue(buildFieldName('currentCustomerItemDiscount'), newProduct.currentCustomerItemDiscount);

      setValue(buildFieldName('percentileBaseMargin'), newProduct.percentileBaseMargin);
      setValue(buildFieldName('adjustedBaseMargin'), newProduct.adjustedBaseMargin);
      setValue(buildFieldName('increaseCap'), newProduct.increaseCap);
      setValue(buildFieldName('ttmMargin'), newProduct.ttmMargin);

      clearErrors('products');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box sx={rootStyles}>
      {executeMutation.isLoading ? (
        <CircularProgress />
      ) : (
        <Button variant='contained' color='primary' onClick={recalculateProduct}>
          re-calculate
        </Button>
      )}
    </Box>
  );
}
